import { useMemo, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { merge } from "lodash";
import useCsvUploadStore from "../../../state/csv-upload-store";
import { makeSxStyles } from "ui";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgressBorder } from "./CircularProgressBorder";
import { useAdminLocationControllerAcceptUpdatesFromCsv } from "service";
import { toast } from "react-toastify";

interface CustomerManagementIconProps {
  link: {
    text: string;
    path: string;
    param?: string;
    paramValue?: string;
    icon: React.ReactNode;
  };
  open: boolean;
  isTooltipHovered: boolean;
  setIsTooltipHovered: (value: boolean) => void;
  tooltipTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  autoHideTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  setCancelDialogOpen: (value: boolean) => void;
  isCancellingInProgress: boolean;
  onSidebarStateChange?: (isOpen: boolean) => void;
}
export const CustomerManagementIcon = ({
  link,
  open,
  isTooltipHovered,
  setIsTooltipHovered,
  tooltipTimeoutRef,
  autoHideTimeoutRef,
  setCancelDialogOpen,
  isCancellingInProgress,
  onSidebarStateChange,
}: CustomerManagementIconProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useStyles();

  const {
    showTooltip: showUploadTooltip,
    progress: uploadProgress,
    message: uploadMessage,
    status: uploadStatus,
    reviewData,
    isReadyForReview,
    hideTooltip,
    cancelUpload,
    pauseProgressUpdates,
    openModal,
  } = useCsvUploadStore();

  // Track state changes
  const prevStatusRef = useRef(uploadStatus);
  const prevReviewDataRef = useRef(reviewData);
  const prevOpenRef = useRef(open);

  // Force tooltip repositioning when sidebar state changes
  const [tooltipKey, setTooltipKey] = useState(0);

  // Prevent tooltip from showing immediately after sidebar state change
  const [sidebarChanged, setSidebarChanged] = useState(false);

  // Force tooltip to recalculate position by temporarily hiding and showing it
  const [forceReposition, setForceReposition] = useState(false);

  // Refs for managing sidebar change timeouts
  const sidebarChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const repositionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to clear all timeouts
  const clearAllTimeouts = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
    if (sidebarChangeTimeoutRef.current) {
      clearTimeout(sidebarChangeTimeoutRef.current);
      sidebarChangeTimeoutRef.current = null;
    }
    if (repositionTimeoutRef.current) {
      clearTimeout(repositionTimeoutRef.current);
      repositionTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (
      prevStatusRef.current !== uploadStatus ||
      prevReviewDataRef.current !== reviewData
    ) {
      prevStatusRef.current = uploadStatus;
      prevReviewDataRef.current = reviewData;
    }
  }, [uploadStatus, reviewData]);

  // Handle sidebar state changes to properly manage tooltip
  useEffect(() => {
    if (prevOpenRef.current !== open) {
      prevOpenRef.current = open;

      // Clear any existing timeouts
      clearAllTimeouts();

      // When sidebar opens or closes, reset hover state to prevent stuck tooltips
      setIsTooltipHovered(false);

      // Prevent tooltip from showing immediately after state change
      setSidebarChanged(true);

      // First timeout: wait for sidebar transition to complete
      sidebarChangeTimeoutRef.current = setTimeout(() => {
        setSidebarChanged(false);
        setForceReposition(true);

        // Second timeout: finalize repositioning
        repositionTimeoutRef.current = setTimeout(() => {
          setForceReposition(false);
          setTooltipKey((prev) => prev + 1);
          repositionTimeoutRef.current = null;
        }, 50);

        sidebarChangeTimeoutRef.current = null;
      }, 200);

      // Notify parent component of state change
      onSidebarStateChange?.(open);
    }

    // Cleanup function
    return () => {
      if (sidebarChangeTimeoutRef.current) {
        clearTimeout(sidebarChangeTimeoutRef.current);
        sidebarChangeTimeoutRef.current = null;
      }
      if (repositionTimeoutRef.current) {
        clearTimeout(repositionTimeoutRef.current);
        repositionTimeoutRef.current = null;
      }
    };
  }, [open, setIsTooltipHovered, onSidebarStateChange]);

  const hasUploadState =
    uploadStatus === "processing" ||
    uploadStatus === "ready-for-review" ||
    uploadStatus === "applying-changes";

  const isInReviewState = uploadStatus === "ready-for-review";

  const isApplyingChanges = uploadStatus === "applying-changes";

  const hasActiveUpload =
    uploadStatus === "processing" ||
    uploadStatus === "ready-for-review" ||
    uploadStatus === "applying-changes";

  // Handle tooltip repositioning when sidebar is stable (closed) and tooltip should show
  useEffect(() => {
    let repositionTimer: NodeJS.Timeout | null = null;

    if (
      !open &&
      !sidebarChanged &&
      hasActiveUpload &&
      (isTooltipHovered || showUploadTooltip)
    ) {
      // Force complete tooltip reset and repositioning by temporarily hiding it
      setForceReposition(true);
      repositionTimer = setTimeout(() => {
        setForceReposition(false);
        // Change key to force complete remount
        setTooltipKey((prev) => prev + 1);
      }, 50);
    }

    // Cleanup function
    return () => {
      if (repositionTimer) {
        clearTimeout(repositionTimer);
      }
    };
  }, [
    open,
    sidebarChanged,
    hasActiveUpload,
    isTooltipHovered,
    showUploadTooltip,
  ]);

  // Cleanup all timeouts when component unmounts
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  const shouldShowTooltip =
    !open &&
    !sidebarChanged &&
    !forceReposition &&
    hasActiveUpload &&
    (isTooltipHovered || showUploadTooltip);

  const isProcessingWithProgress = uploadStatus === "processing";

  const shouldShowGreenBackground = useMemo(() => {
    return (
      shouldShowTooltip ||
      isTooltipHovered ||
      isInReviewState ||
      isApplyingChanges
    );
  }, [shouldShowTooltip, isTooltipHovered, isInReviewState, isApplyingChanges]);

  const shouldShowProgressBorder = useMemo(() => {
    return isProcessingWithProgress && !isTooltipHovered;
  }, [isProcessingWithProgress, isTooltipHovered]);

  const handleTooltipOpen = () => {
    setIsTooltipHovered(true);
    // Clear tooltip-related timeouts when opening
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
      autoHideTimeoutRef.current = null;
    }
  };

  const handleTooltipClose = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    tooltipTimeoutRef.current = setTimeout(() => {
      setIsTooltipHovered(false);
      tooltipTimeoutRef.current = null;
    }, 2000);
  };

  const iconButton = (
    <Box sx={{ position: "relative" }}>
      <IconButton
        key={link.path}
        sx={{
          padding: "12px",
          marginBottom: "12px",
          borderRadius: "24px",
          gap: "12px",
          justifyContent: "flex-start",
          backgroundColor: shouldShowGreenBackground
            ? "#005140"
            : "transparent",
          color: "#60DBB9",
          "&:hover": {
            backgroundColor: shouldShowGreenBackground
              ? "#005140"
              : "rgba(96, 219, 185, 0.1)",
          },
          ...(location.pathname.includes(link.path) && {
            background: shouldShowGreenBackground ? "#005140" : "#1976d2",
          }),
        }}
        onClick={() =>
          navigate(
            link.param && link.paramValue
              ? link.path.replace(link.param, link.paramValue)
              : link.path,
          )
        }
        onMouseEnter={handleTooltipOpen}
        onMouseLeave={handleTooltipClose}
        data-testid={link.text}
        data-isolated-customer-management="upload" // Unique identifier
      >
        <Box
          sx={{
            color: "#60DBB9", // Always green during upload states
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {link.icon}
        </Box>
        {open && (
          <Typography
            sx={merge(styles.buttonText, { color: "inherit" })}
            variant="body1"
          >
            {link.text}
          </Typography>
        )}
      </IconButton>

      {shouldShowProgressBorder && (
        <CircularProgressBorder progress={uploadProgress} />
      )}
    </Box>
  );

  const { mutate: cancelCsvUploadInReview, isLoading: isCancellingInReview } =
    useAdminLocationControllerAcceptUpdatesFromCsv({
      onSuccess: () => {
        // Check if this is an accept operation from the main page
        const currentState = useCsvUploadStore.getState();

        if (currentState.isAcceptingFromMainPage) {
          // Reset the flag but don't cancel - let main page handle it
          useCsvUploadStore.getState().setAcceptingFromMainPage(false);
          return;
        }

        // This is a real cancel operation from sidebar
        toast.success("Upload cancelled successfully");
        cancelUpload();
      },
      onError: () => {
        // Reset the flag on error
        useCsvUploadStore.getState().setAcceptingFromMainPage(false);
        toast.error("Failed to cancel upload. Please try again.");
      },
    });

  const handleCancelUpload = () => {
    if (uploadStatus === "processing") {
      pauseProgressUpdates();
      setCancelDialogOpen(true);
    } else {
      cancelUpload();
      cancelCsvUploadInReview({
        body: {
          newAddressAndCustomer: false,
          existingAddressNewCustomer: false,
          updateCustomerOnExistingAddress: false,
          updateAddressOfExistingCustomer: false,
          existingCustomerNewAddress: false,
        },
      });
    }
  };

  const handleReviewDetails = () => {
    hideTooltip();

    // Open the modal directly from any page - no navigation required
    if (reviewData) {
      openModal(reviewData, "success");
    }
  };

  const stableProgress = useMemo(() => {
    const rounded = Math.floor(uploadProgress / 10) * 10;
    return rounded;
  }, [Math.floor(uploadProgress / 10)]);

  const displayProgress = Math.round(uploadProgress);

  const uploadTooltipContent = useMemo(
    () => (
      <Box sx={styles.uploadTooltip}>
        <Typography variant="titleSmall">{uploadMessage}</Typography>

        {isApplyingChanges ? (
          // Applying changes state - no buttons, just info and spinner
          <>
            <Typography variant="bodySmall" sx={{ mb: "8px" }}>
              The update is happening in the background and will finish shortly.
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#60DBB9" }} />
            </Box>
          </>
        ) : isReadyForReview ? (
          // Ready for review state
          <>
            <Typography variant="bodySmall" sx={{ mb: "8px" }}>
              Review changes before importing.
            </Typography>
            <Box sx={styles.uploadTooltipFooter}>
              <Box /> {/* Empty box for spacing */}
              <Button
                variant="contained"
                size="small"
                onClick={handleReviewDetails}
              >
                Review Details
              </Button>
            </Box>
          </>
        ) : (
          // Processing state
          <>
            <Typography variant="bodySmall" sx={{ mb: "8px" }}>
              You can keep working while we process your file.
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stableProgress} // Use stable progress to prevent jumping
              sx={styles.uploadTooltipProgress}
            />
            <Box sx={styles.uploadTooltipFooter}>
              <Typography variant="bodySmall">{displayProgress}%</Typography>
              <Button
                variant="text"
                size="small"
                onClick={handleCancelUpload}
                disabled={isCancellingInProgress || isCancellingInReview}
              >
                {isCancellingInProgress || isCancellingInReview
                  ? "Cancelling..."
                  : "Cancel Upload"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    ),
    [
      uploadMessage,
      isApplyingChanges,
      isReadyForReview,
      stableProgress, // Use stable progress instead of raw progress
      Math.floor(displayProgress / 5) * 5, // Only update display when it changes by 5%
      isCancellingInProgress,
      isCancellingInReview,
      handleReviewDetails,
      handleCancelUpload,
      styles.uploadTooltip,
      styles.uploadTooltipFooter,
      styles.uploadTooltipProgress,
    ],
  );

  if (!hasUploadState) {
    return (
      <IconButton
        key={link.path}
        className={location.pathname.includes(link.path) ? "active" : ""}
        sx={merge(
          styles.sidebarItem,
          location.pathname.includes(link.path) && styles.activeSidebarItem,
        )}
        onClick={() =>
          navigate(
            link.param && link.paramValue
              ? link.path.replace(link.param, link.paramValue)
              : link.path,
          )
        }
        data-testid={link.text}
        data-isolated-customer-management="normal"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {link.icon}
        </Box>
        {open && (
          <Typography sx={styles.buttonText} variant="body1">
            {link.text}
          </Typography>
        )}
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip
        key={`${link.path}-tooltip-${tooltipKey}`}
        title={shouldShowTooltip ? uploadTooltipContent : ""}
        placement="right"
        arrow
        open={shouldShowTooltip}
        disableHoverListener
        enterDelay={100}
        leaveDelay={0}
        PopperProps={{
          onMouseEnter: handleTooltipOpen,
          onMouseLeave: handleTooltipClose,
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["left", "top", "bottom"],
              },
            },
          ],
          sx: {
            "& .MuiTooltip-tooltip": {
              backgroundColor: "#282A2D",
              backgroundImage: "none",
              padding: 0,
              maxWidth: "310px",
              transition: "none", // Disable transition to prevent positioning issues
            },
            "& .MuiTooltip-arrow": {
              color: "#282A2D",
            },
          },
        }}
      >
        <Box onMouseEnter={handleTooltipOpen} onMouseLeave={handleTooltipClose}>
          {iconButton}
        </Box>
      </Tooltip>
    </>
  );
};

const useStyles = makeSxStyles(
  ({
    palette: {
      background: { paper },
      primary: { main, dark },
    },
  }) => ({
    container: {
      background: paper,
      position: "fixed",
      zIndex: 1003,
      maxWidth: "250px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      height: "calc(100vh - 56px)",
      width: "64px",
      transition: "width 0.2s ease-in-out",
    },
    containerOpen: {
      width: "250px",
    },
    sidebarItem: {
      padding: "12px",
      mb: "12px",
      borderRadius: "24px",
      gap: "12px",
      justifyContent: "flex-start",
    },
    activeSidebarItem: {
      "&.active": {
        background: dark,
      },
    },
    stickyMenu: {
      position: "sticky",
      top: 0,
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "stretch",
      paddingTop: "24px",
    },
    button: {
      width: "50px",
      height: "50px",
    },
    icon: {
      width: "25px",
      height: "25px",
    },
    activeButton: {
      background: main,
      color: "black",
    },
    buttonText: {
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    uploadTooltip: {
      padding: "14px",
      minWidth: "310px",
      maxWidth: "310px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    uploadTooltipProgress: {
      height: "4px",
    },
    uploadTooltipFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
  }),
);
